import React, { useState } from 'react'
import { Box, Grid, InputLabel } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'

import { Container, Page, ServiceUnreachableAlert } from 'components'
import {
  CookiesConsentsForm,
  ExportFormatDialog,
  Header,
  ReportVersionTable,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import useBrowserReachability from 'hooks/useBrowserReachability'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import { schema } from './components/CookiesConsentsForm'

import useStyles from './styles'

const CookiesConsentsReport = () => {
  const [currentReportVersion, setCurrentReportVersion] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [refresher, setRefresher] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const cookiesService = useBrowserReachability(
    constants.externalServices.COOKIES_KEY,
  )

  const formMethods = useForm({
    validationSchema: schema(),
    defaultValues: {
      reportSelected: '',
      bannerId: null,
      consentType: '',
      startDate: null,
      endDate: null,
      exportFormat: constants.reports.EXPORT_FORMAT_TYPES.PDF,
    },
  })

  const { handleSubmit, triggerValidation, watch } = formMethods

  const { reportSelected, bannerId, consentType, startDate, endDate } = watch()

  const { REPORT_TYPES } = constants.cookies.reports
  const { EXPORT_FORMAT_TYPES } = constants.reports

  const handleRefresh = () => setRefresher((prev) => !prev)

  const buildExportParams = (shouldCreateVersion, exportFormat) => {
    const params = {
      reportType: reportSelected,
      exportFormat,
      shouldCreateVersion,
    }

    if (reportSelected === REPORT_TYPES.COMPLETE) {
      params.complete = true
      return params
    }

    if (bannerId?.id) {
      params.bannerId = bannerId.id
    }
    if (consentType) {
      params.kind = consentType
    }
    if (startDate) {
      params.datetimeConsentGteq = helpers.formatters.date(
        startDate,
        'YYYY-MM-DD',
      )
    }
    if (endDate) {
      params.datetimeConsentLteq = helpers.formatters.date(
        endDate,
        'YYYY-MM-DD',
      )
    }

    return params
  }

  const exportReport = async (shouldCreateVersion, exportFormat) => {
    setExporting(true)
    try {
      await service.dponet.cookieConsents.exportReport(
        buildExportParams(shouldCreateVersion, exportFormat),
      )

      const isExcel = exportFormat === EXPORT_FORMAT_TYPES.EXCEL

      snackbar.open({
        message: `O relatório ${
          isExcel ? 'Excel ' : ''
        }será gerado e enviado por e-mail!`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            'Houve um erro durante o processamento de dados.',
        ),
        variant: 'error',
      })
    } finally {
      setExporting(false)
      setTimeout(handleRefresh, 8000)
    }
  }

  const handleCloseExportFormatDialog = () => setDialogOpen(false)

  const handleOpenExportFormatDialog = async () => {
    const isValid = await triggerValidation()
    if (!isValid) return

    setDialogOpen(true)
  }

  const onConfirmExport = async (data) => {
    handleCloseExportFormatDialog()

    const isDestroyAction =
      data?.dataAction === constants.reportVersions.DESTROY_VERSION_ID_ACTION

    if (isDestroyAction && currentReportVersion?.id) {
      try {
        await service.dponet.reportVersions.destroy({
          reportVersionId: currentReportVersion.id,
        })
        setCurrentReportVersion(null)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error ||
              'Falha ao remover versão do relatório.',
          ),
          variant: 'error',
        })
      }
    }

    await exportReport(true, data.exportFormat)
  }

  const { cookiesConsents } = constants.reports.DESCRIPTION

  return (
    <FormContext {...formMethods}>
      <Page
        className={classes.root}
        title="Relatório de Consentimento de Cookies"
      >
        <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
          <Box py={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Header
                  type="cookiesConsents"
                  reportTour="cookiesConsents"
                  isLoading={exporting}
                  buttonAction={handleOpenExportFormatDialog}
                  disabled={exporting || !cookiesService.isReachable}
                  title={cookiesConsents.title}
                  description={cookiesConsents.content}
                  buttonType="button"
                />
              </Grid>
              <Grid item xs={12}>
                <ServiceUnreachableAlert
                  services={[constants.externalServices.COOKIES_KEY]}
                />
                <InputLabel className={classes.label}>
                  Filtros de pesquisa:
                </InputLabel>
                <CookiesConsentsForm />
              </Grid>
              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.COOKIES_CONSENTS}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
          </Box>
          <ExportFormatDialog
            open={dialogOpen}
            onClose={handleCloseExportFormatDialog}
            onConfirmExport={handleSubmit(onConfirmExport)}
            isLoadingExcel={exporting}
            currentReportVersion={currentReportVersion}
            alertMessage="O relatório em PDF é limitado a 5.000 consentimentos. Para volumes maiores, utilize o formato Excel."
          />
        </Container>
      </Page>
    </FormContext>
  )
}

export default CookiesConsentsReport
